import {
  PostStatus,
  type OwnerCollectionsPermissionsGroup,
  type PostProps
} from 'src/hook/usePosts'
import { useUser } from 'src/hook/useUser'
import type { PermissionActions } from 'src/services/posts'
import { CommunityMemberPermissionsGroup } from 'src/services/community'
import {
  canUserBanAdmin,
  canUserBanMember,
  canUserBanModerator,
  filterPermission,
  isAuthorMemberTypeOwnerOrAdmin
} from 'src/utils/user'
import { PERMISSIONS_ACTIONS } from 'src/utils/user/constant'

export type usePermissionsProps = {
  post: PostProps
}

const usePermissions = ({ post }: usePermissionsProps) => {
  const { permissionsUser, hasPermissionToManage } = useUser()

  const hasPermissionToBan = [
    !hasPermissionToManage(permissionsUser?.group as CommunityMemberPermissionsGroup) ||
      post.isAuthor,

    isAuthorMemberTypeOwnerOrAdmin(post.authorMemberType as OwnerCollectionsPermissionsGroup) &&
      !canUserBanAdmin(permissionsUser?.actions as PermissionActions[]),

    (post.authorMemberType as CommunityMemberPermissionsGroup) ===
      CommunityMemberPermissionsGroup.MODERATOR &&
      !canUserBanModerator(permissionsUser?.actions as PermissionActions[]),

    !canUserBanMember(permissionsUser?.actions as PermissionActions[])
  ].every((value) => value === false)

  const canDeleteScheduledPost = filterPermission({
    specificPermission: PERMISSIONS_ACTIONS.SCHEDULED_POST_DELETE as PermissionActions,
    permissionsUserList: permissionsUser?.actions as PermissionActions[]
  })

  const canPublishScheduledPost = Boolean(
    filterPermission({
      specificPermission: PERMISSIONS_ACTIONS.SCHEDULED_POST_CREATE as PermissionActions,
      permissionsUserList: permissionsUser?.actions as PermissionActions[]
    }) && post?.postStatus === PostStatus.SCHEDULED
  )

  const canDeletePost =
    filterPermission({
      specificPermission: PERMISSIONS_ACTIONS.POST_DELETE,
      permissionsUserList: permissionsUser?.actions as PermissionActions[]
    }) || post.isAuthor

  const canPinPost =
    (filterPermission({
      specificPermission: PERMISSIONS_ACTIONS.PIN_CREATE,
      permissionsUserList: permissionsUser?.actions as PermissionActions[]
    }) &&
      post?.postStatus !== PostStatus.SCHEDULED) ||
    false

  const canUnPinPost =
    (filterPermission({
      specificPermission: PERMISSIONS_ACTIONS.PIN_DELETE,
      permissionsUserList: permissionsUser?.actions as PermissionActions[]
    }) &&
      post?.postStatus !== PostStatus.SCHEDULED) ||
    false

  return {
    hasPermissionToBan,
    canDeleteScheduledPost,
    canPublishScheduledPost,
    canDeletePost,
    canPinPost,
    canUnPinPost
  }
}

export default usePermissions
